import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Logo } from "../../components/site/Seo";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, ArrowRight } from "lucide-react";

export default function AuthPage({ mode }) {
  const { login, register, otpVerify } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/student/dashboard";
  const [tab, setTab] = useState(mode === "register" ? "register" : "login");
  const [method, setMethod] = useState("password"); // password | otp
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "" });
  const [otp, setOtp] = useState({ mobile: "", code: "", name: "", sent: false, devCode: "" });
  const [busy, setBusy] = useState(false);

  const go = (u) => nav(u.role === "student" ? next : "/admin", { replace: true });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = tab === "login" ? await login(form.email, form.password) : await register(form);
      toast.success(tab === "login" ? "Welcome back!" : "Account created!");
      go(u);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/request", { mobile: otp.mobile });
      setOtp({ ...otp, sent: true, devCode: data.dev_code || "" });
      toast.success(data.dev_code ? `OTP (demo): ${data.dev_code}` : "OTP sent to your mobile");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await otpVerify({ mobile: otp.mobile, code: otp.code, name: otp.name });
      toast.success("Signed in!");
      go(u);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center container-px py-10 hero-glow">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8"><Logo className="h-9" /></Link>
        <div className="card-surface p-7">
          <div className="flex gap-2 p-1 bg-ink rounded-full mb-6">
            <button onClick={() => setTab("login")} className={`flex-1 py-2 rounded-full text-sm font-medium transition ${tab === "login" ? "btn-ember" : "text-content-secondary"}`} data-testid="auth-tab-login">Login</button>
            <button onClick={() => setTab("register")} className={`flex-1 py-2 rounded-full text-sm font-medium transition ${tab === "register" ? "btn-ember" : "text-content-secondary"}`} data-testid="auth-tab-register">Register</button>
          </div>

          <div className="flex gap-4 text-xs mb-5">
            <button onClick={() => setMethod("password")} className={method === "password" ? "text-ember font-medium" : "text-content-muted"}>Email & Password</button>
            <button onClick={() => setMethod("otp")} className={method === "otp" ? "text-ember font-medium" : "text-content-muted"} data-testid="auth-otp-toggle">Mobile OTP</button>
          </div>

          {method === "password" ? (
            <form onSubmit={submit} className="space-y-3">
              {tab === "register" && <Field icon={User} placeholder="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="auth-name" required />}
              <Field icon={Mail} type="email" placeholder="Email address" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="auth-email" required />
              {tab === "register" && <Field icon={Phone} placeholder="Mobile (optional)" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testid="auth-mobile" />}
              <Field icon={Lock} type="password" placeholder="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="auth-password" required />
              {tab === "login" && <div className="text-right"><Link to="/forgot-password" className="text-xs text-content-muted hover:text-ember">Forgot password?</Link></div>}
              <Button disabled={busy} className="btn-ember rounded-full w-full h-11" data-testid="auth-submit">{busy ? "Please wait…" : tab === "login" ? "Login" : "Create account"} <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-3">
              <Field icon={Phone} placeholder="10-digit mobile number" value={otp.mobile} onChange={(v) => setOtp({ ...otp, mobile: v })} testid="auth-otp-mobile" required />
              {!otp.sent ? (
                <Button type="button" disabled={busy} onClick={sendOtp} className="btn-ember rounded-full w-full h-11" data-testid="auth-otp-send">Send OTP</Button>
              ) : (
                <>
                  <Field icon={User} placeholder="Your name (new users)" value={otp.name} onChange={(v) => setOtp({ ...otp, name: v })} />
                  <Field icon={Lock} placeholder="Enter 6-digit OTP" value={otp.code} onChange={(v) => setOtp({ ...otp, code: v })} testid="auth-otp-code" required />
                  {otp.devCode && <p className="text-xs text-content-muted">Demo mode — OTP: <span className="text-ember font-mono">{otp.devCode}</span></p>}
                  <Button disabled={busy} className="btn-ember rounded-full w-full h-11" data-testid="auth-otp-verify">Verify & continue</Button>
                </>
              )}
            </form>
          )}
        </div>
        <p className="text-center text-xs text-content-muted mt-5">By continuing you agree to our <Link to="/terms-and-conditions" className="text-ember">Terms</Link> & <Link to="/privacy-policy" className="text-ember">Privacy Policy</Link>.</p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, type = "text", placeholder, value, onChange, testid, required }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
      <Input type={type} placeholder={placeholder} value={value} required={required}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="pl-9 bg-ink border-line h-11" />
    </div>
  );
}
